import { AdSenseAd } from "@/components/ads/AdSenseAd";
import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { type BreadcrumbItem } from "@/components/layout/breadcrumb-defs";
import { CloneRecipeDialog } from "@/components/recipes/CloneRecipeDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useRecipe } from "@/hooks/use-recipe";
import { useRecipeBox, useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useToast } from "@/hooks/use-toast";
import { trackRecipeCloned, trackRecipeDeleted, trackRecipeShared, trackRecipeUpdated } from "@/lib/analytics-service";
import { canAddRecipes, canDeleteRecipe, canEditRecipe, canExportRecipes } from "@/lib/recipe-box-permissions";
import { cloneRecipe, deleteRecipe, updateRecipe } from "@/lib/recipe-service";
import type { Recipe } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RecipeEditForm } from "./recipe/_components/RecipeEditForm";
import { RecipeHeader } from "./recipe/_components/RecipeHeader";
import { RecipeImageEditDialog } from "./recipe/_components/RecipeImageEditDialog";
import { RecipeMedia } from "./recipe/_components/RecipeMedia";
import { RecipeMetaSidebar } from "./recipe/_components/RecipeMetaSidebar";
import { RecipeReadOnlyContent } from "./recipe/_components/RecipeReadOnlyContent";
import { toDate } from "./recipe/_lib/date";
import { recipeFormSchema, type RecipeFormValues } from "./recipe/_lib/recipeForm";

const FALLBACK_RECIPE_IMAGE = "https://picsum.photos/seed/recipe-detail/960/540";

const buildIngredientsList = (recipe: Recipe | null) => recipe?.ingredients ?? [];

const buildDirectionsList = (recipe: Recipe | null) =>
  recipe?.directions
    ?.split(/\r?\n/)
    .map((step) => step.trim())
    .filter(Boolean) ?? [];

export default function RecipePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  
  const { recipe, loading, error } = useRecipe(id);
  const { box: recipeBox } = useRecipeBox(recipe?.recipeBoxId ?? null);
  const { user } = useAuthProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  const recipeForm = useForm<RecipeFormValues>({
    resolver: zodResolver(recipeFormSchema),
    defaultValues: {
      title: "",
      labels: [],
      ingredients: "",
      directions: "",
      notes: "",
      prepTime: "",
      cookTime: "",
      servings: "",
      imageHint: "",
      isPublic: false,
      sharePassword: "",
      isDraft: false,
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [startInEdit] = useState(mode === "edit");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [imageEditDialogOpen, setImageEditDialogOpen] = useState(false);

  const { profile } = useAuthProfile();
  const { boxes: userRecipeBoxes } = useRecipeBoxes(user?.uid);
  const canEdit = recipe && recipeBox && user ? canEditRecipe(recipeBox, recipe, user.uid) : false;
  const canDelete = recipeBox && user ? canDeleteRecipe(recipeBox, user.uid) : false;
  const canExport = recipeBox && user ? canExportRecipes(recipeBox, user.uid) : false;
  const canClone = recipeBox && user ? canAddRecipes(recipeBox, user.uid) : false;
  const accountTier = profile?.accountTier;

  useEffect(() => {
    if (startInEdit && recipe && canEdit) {
      setIsEditing(true);
    }
  }, [startInEdit, recipe, canEdit]);

  const prevIsEditingRef = useRef(false);

  useEffect(() => {
    if (!recipe) {
      return;
    }

    const justEnteredEditMode = isEditing && !prevIsEditingRef.current;
    prevIsEditingRef.current = isEditing;

    // Reset the form only when:
    // 1. Not in edit mode (recipe loaded/changed while viewing)
    // 2. User just entered edit mode (populate form with current values)
    // Never reset while actively editing — Firestore subscription fires new object
    // references on every snapshot which would wipe the user's in-progress changes.
    if (isEditing && !justEnteredEditMode) {
      return;
    }

    recipeForm.reset({
      title: recipe.title,
      labels: recipe.labels ?? [],
      ingredients: recipe.ingredients.join("\n"),
      directions: recipe.directions,
      notes: recipe.notes ?? "",
      prepTime: recipe.prepTime ?? "",
      cookTime: recipe.cookTime ?? "",
      servings: recipe.servings ?? "",
      imageHint: recipe.imageHint ?? "",
      isPublic: recipe.isPublic ?? false,
      sharePassword: "", // Don't populate password field for security
      isDraft: recipe.isDraft ?? false,
    });
    setImageFile(null);
    setImagePreviewUrl(null);
    setRemoveImage(false);
  }, [recipe, recipeForm, isEditing]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return;
    }

    const preview = URL.createObjectURL(imageFile);
    setImagePreviewUrl(preview);

    return () => {
      URL.revokeObjectURL(preview);
    };
  }, [imageFile]);

  const currentImageUrl = useMemo(() => {
    if (removeImage) {
      return null;
    }
    if (imagePreviewUrl) {
      return imagePreviewUrl;
    }
    if (recipe?.imageUrl) {
      return recipe.imageUrl;
    }
    return FALLBACK_RECIPE_IMAGE;
  }, [imagePreviewUrl, recipe?.imageUrl, removeImage]);


  const createdDate = useMemo(() => toDate(recipe?.createdAt), [recipe?.createdAt]);



  const ingredientsList = useMemo(() => buildIngredientsList(recipe), [recipe]);
  const directionsList = useMemo(() => buildDirectionsList(recipe), [recipe]);

  const updatedDate = useMemo(() => toDate(recipe?.updatedAt), [recipe?.updatedAt]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: "Recipe Boxes", href: "/home" }];

    if (recipe?.recipeBoxId) {
      items.push({ label: recipeBox?.title || "Recipe Box", href: `/recipe-box/${recipe.recipeBoxId}` });
    }

    if (recipe?.title) {
      items.push({ label: recipe.title });
    }

    return items;
  }, [recipe?.recipeBoxId, recipe?.title, recipeBox?.title]);

  usePageBreadcrumbs(breadcrumbItems);

  if (!id) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <h2 className="font-headline text-xl">Recipe ID is missing</h2>
      </div>
    );
  }

  const handleSubmit = recipeForm.handleSubmit(async (values) => {
    if (!recipe || !user) {
      return;
    }

    const ingredients = values.ingredients
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    try {
      await updateRecipe(user.uid, recipe.id, {
        title: values.title,
        labels: values.labels,
        ingredients,
        directions: values.directions,
        notes: values.notes,
        prepTime: values.prepTime,
        cookTime: values.cookTime,
        servings: values.servings,
        imageHint: values.imageHint,
        imageFile: imageFile ?? undefined,
        removeImage,
        isPublic: values.isPublic,
        sharePassword: values.sharePassword && values.sharePassword.trim().length > 0
          ? values.sharePassword
          : undefined,
        isDraft: values.isDraft,
      }, recipeBox);

      trackRecipeUpdated();

      toast({
        title: "Recipe updated",
        description: "Your changes have been saved.",
      });

      setIsEditing(false);
      setImageFile(null);
      setRemoveImage(false);
    } catch (updateError) {
      console.error("Failed to update recipe", updateError);
      toast({
        variant: "destructive",
        title: "Could not save changes",
        description: "Please try again.",
      });
    }
  });

  const handleDelete = async () => {
    if (!recipe) {
      return;
    }

    setIsDeleting(true);
    try {
      if (!user) {
        return;
      }
      await deleteRecipe(recipe.recipeBoxId, recipe.id, recipe.imageStoragePath, recipeBox, user.uid);
      trackRecipeDeleted();
      toast({
        title: "Recipe deleted",
        description: "We removed this recipe from the box.",
      });
      navigate(`/recipe-box/${recipe.recipeBoxId}`);
    } catch (deleteError) {
      console.error("Failed to delete recipe", deleteError);
      toast({
        variant: "destructive",
        title: "Could not delete recipe",
        description: "Please try again.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShare = async () => {
    if (!recipe) {
      return;
    }

    // Check if recipe is public and has a share token
    if (recipe.isPublic && recipe.shareToken) {
      const { getRecipeShareUrl } = await import("@/lib/recipe-sharing-service");
      const shareUrl = getRecipeShareUrl(recipe.shareToken);
      
      const { navigator } = globalThis;
      if (!navigator?.clipboard) {
        toast({
          variant: "destructive",
          title: "Unable to copy link",
          description: "Clipboard access is not available in this environment.",
        });
        return;
      }

      try {
        await navigator.clipboard.writeText(shareUrl);
        trackRecipeShared(recipe.isPublic ?? false, !!recipe.sharePassword);
        toast({
          title: "Share link copied",
          description: "Share this public link with anyone!",
        });
      } catch (copyError) {
        console.error("Failed to copy share link", copyError);
        toast({
          variant: "destructive",
          title: "Could not copy link",
          description: "Please try again.",
        });
      }
    } else {
      // Recipe is not public - prompt user to make it public
      toast({
        variant: "default",
        title: "Recipe is not public",
        description: "Edit the recipe and enable 'Make this recipe publicly shareable' to get a share link.",
      });
    }
  };

  const handlePrint = () => {
    if (!recipe) {
      return;
    }

    // Create a print-friendly version of the recipe
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast({
        variant: "destructive",
        title: "Print blocked",
        description: "Please allow popups to print this recipe.",
      });
      return;
    }

    const isFreeTier = accountTier === "free" || !accountTier;



    const metadata: string[] = [];
    if (recipe.prepTime) metadata.push(`Prep: ${recipe.prepTime}`);
    if (recipe.cookTime) metadata.push(`Cook: ${recipe.cookTime}`);
    if (recipe.servings) metadata.push(`Serves: ${recipe.servings}`);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${recipe.title}</title>
          <style>
            @media print {
              @page {
                margin: 1in;
              }
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              line-height: 1.6;
            }
            h1 {
              font-size: 2em;
              margin-bottom: 10px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .metadata {
              color: #666;
              margin-bottom: 20px;
            }
            .section {
              margin: 30px 0;
            }
            .section h2 {
              font-size: 1.5em;
              margin-bottom: 10px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .ingredients {
              list-style: none;
              padding-left: 0;
            }
            .ingredients li {
              margin: 5px 0;
            }
            .directions {
              counter-reset: step-counter;
            }
            .directions li {
              counter-increment: step-counter;
              list-style: none;
              margin: 10px 0;
              padding-left: 30px;
              position: relative;
            }
            .directions li::before {
              content: counter(step-counter) ".";
              position: absolute;
              left: 0;
              font-weight: bold;
            }
            .notes {
              font-style: italic;
              color: #555;
              margin-top: 20px;
              padding: 15px;
              background: #f5f5f5;
              border-left: 4px solid #333;
            }
            .ad {
              margin-top: 40px;
              padding: 20px;
              background: #fff3cd;
              border: 2px solid #ffc107;
              border-radius: 5px;
              text-align: center;
            }
            .ad h3 {
              margin-top: 0;
              color: #856404;
            }
            .ad p {
              color: #856404;
              margin: 10px 0;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ccc;
              text-align: center;
              color: #666;
              font-size: 0.9em;
            }
          </style>
        </head>
        <body>
          <h1>${recipe.title}</h1>
          ${recipeBox ? `<div class="metadata">From: ${recipeBox.title}</div>` : ""}
          ${metadata.length > 0 ? `<div class="metadata">${metadata.join(" • ")}</div>` : ""}
          ${recipe.labels.length > 0 ? `<div class="metadata">Labels: ${recipe.labels.join(", ")}</div>` : ""}
          
          <div class="section">
            <h2>Ingredients</h2>
            <ul class="ingredients">
              ${recipe.ingredients.map((ing) => `<li>• ${ing}</li>`).join("")}
            </ul>
          </div>
          
          <div class="section">
            <h2>Directions</h2>
            <ol class="directions">
              ${recipe.directions
                .split(/\r?\n/)
                .filter(Boolean)
                .map((step) => `<li>${step}</li>`)
                .join("")}
            </ol>
          </div>
          
          ${recipe.notes ? `<div class="notes"><strong>Notes:</strong><br>${recipe.notes}</div>` : ""}
          
          ${isFreeTier ? `
            <div class="ad">
              <h3>Upgrade to ezRecipeBox Pro</h3>
              <p>Remove ads and unlock unlimited recipes!</p>
              <p>Visit your profile to upgrade today.</p>
            </div>
          ` : ""}
          
          <div class="footer">
            Printed from ezRecipeBox on ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleImageSelected = (file: File | null) => {
    setImageFile(file);
    setRemoveImage(false);
  };

  const toggleRemoveImage = () => {
    setRemoveImage((previous) => {
      const next = !previous;
      if (!previous && next) {
        setImageFile(null);
      }
      return next;
    });
  };



  const handleImageEditClick = () => {
    if (canEdit) {
      setImageEditDialogOpen(true);
    }
  };

  const handleImageSave = async () => {
    if (!recipe || !user) {
      return;
    }

    try {
      await updateRecipe(user.uid, recipe.id, {
        imageFile: imageFile ?? undefined,
        removeImage,
      }, recipeBox);

      toast({
        title: "Recipe image updated",
        description: "Your image has been saved.",
      });

      setImageFile(null);
      setImagePreviewUrl(null);
      setRemoveImage(false);
      setImageEditDialogOpen(false);
    } catch (updateError) {
      console.error("Failed to update recipe image", updateError);
      toast({
        variant: "destructive",
        title: "Could not save image",
        description: "Please try again.",
      });
    }
  };

  const handleImageEditCancel = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
    setRemoveImage(false);
  };

  const handleCloneSameBox = async () => {
    if (!recipe || !user) {
      return;
    }

    setIsCloning(true);
    try {
      const clonedId = await cloneRecipe(recipe.id, {
        titlePrefix: "Copy of ",
      });
      trackRecipeCloned(true);
      toast({
        title: "Recipe cloned",
        description: "The recipe has been cloned in this recipe box.",
      });
      navigate(`/recipe/${clonedId}`);
    } catch (cloneError) {
      console.error("Failed to clone recipe", cloneError);
      toast({
        variant: "destructive",
        title: "Could not clone recipe",
        description: "Please try again.",
      });
    } finally {
      setIsCloning(false);
    }
  };

  const handleCloneDifferentBox = () => {
    setCloneDialogOpen(true);
  };

  const handleCloneToBox = async (targetRecipeBoxId: string) => {
    if (!recipe || !user) {
      return;
    }

    setIsCloning(true);
    try {
      const clonedId = await cloneRecipe(recipe.id, {
        newRecipeBoxId: targetRecipeBoxId,
        newOwnerUid: user.uid,
        titlePrefix: "Copy of ",
      });
      trackRecipeCloned(false);
      toast({
        title: "Recipe cloned",
        description: "The recipe has been cloned to the selected recipe box.",
      });
      setCloneDialogOpen(false);
      navigate(`/recipe/${clonedId}`);
    } catch (cloneError) {
      console.error("Failed to clone recipe", cloneError);
      toast({
        variant: "destructive",
        title: "Could not clone recipe",
        description: "Please try again.",
      });
    } finally {
      setIsCloning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-64 w-full animate-pulse rounded-lg bg-muted" />
        <div className="h-10 w-64 animate-pulse rounded bg-muted" />
        <div className="h-40 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Unable to load recipe</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!recipe) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <h2 className="font-headline text-xl">Recipe not found</h2>
        <p className="mt-2 text-muted-foreground">It may have been deleted or you no longer have access.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 sm:p-6 lg:p-8">
      {/* Mobile back button - shows on mobile when breadcrumbs are hidden */}
      {recipeBox && recipe.recipeBoxId ? (
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2"
            onClick={() => navigate(`/recipe-box/${recipe.recipeBoxId}`)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Back to {recipeBox.title}
          </Button>
        </div>
      ) : null}
      <RecipeHeader
        recipe={recipe}
        recipeBox={recipeBox}
        createdDate={createdDate}
        updatedDate={updatedDate}
        isOwner={canEdit}
        canDelete={canDelete}
        canExport={canExport}
        canClone={canClone}
        accountTier={accountTier}
        isEditing={isEditing}
        onEnterEdit={() => setIsEditing(true)}
        onExitEdit={() => setIsEditing(false)}
        onShare={handleShare}
        onConfirmDelete={handleDelete}
        onCloneSameBox={handleCloneSameBox}
        onCloneDifferentBox={handleCloneDifferentBox}
        onPrint={handlePrint}
        isDeleting={isDeleting}
        userId={user?.uid ?? null}
        onFavoriteToggle={() => {
          // Update local recipe state if needed
          // The subscription will update automatically
        }}
        onDraftToggle={() => {
          // The subscription will update automatically
        }}
      />

      {recipe && (
        <CloneRecipeDialog
          open={cloneDialogOpen}
          onOpenChange={setCloneDialogOpen}
          recipeTitle={recipe.title}
          sourceRecipeBoxId={recipe.recipeBoxId}
          recipeBoxes={userRecipeBoxes}
          userId={user?.uid ?? null}
          onClone={handleCloneToBox}
          isCloning={isCloning}
        />
      )}

      <div className="grid gap-8 md:grid-cols-5">
        <section className="space-y-8 md:col-span-3">
          {/* Ad above recipe image */}
          <AdSenseAd
            adSlot="2631348401"
            accountTier={accountTier}
            className="mb-4"
          />

          <RecipeMedia
            imageUrl={currentImageUrl}
            title={recipe.title}
            imageHint={recipe.imageHint}
            imageFocalPointX={recipe.imageFocalPointX}
            imageFocalPointY={recipe.imageFocalPointY}
            imageZoom={recipe.imageZoom}
            onClick={handleImageEditClick}
            canEdit={canEdit && !isEditing}
          />

          {/* Ad between image and content */}
          {!isEditing && (
            <AdSenseAd
              adSlot="2631348401"
              accountTier={accountTier}
              className="my-4"
            />
          )}

          {isEditing ? (
            <RecipeEditForm
              form={recipeForm}
              onSubmit={handleSubmit}
              onCancel={() => setIsEditing(false)}

              isSubmitting={recipeForm.formState.isSubmitting}
              boxLabels={recipeBox?.labels}
            />
          ) : (
            <>
              <RecipeReadOnlyContent directions={directionsList} notes={recipe.notes} />
              {/* Ad after recipe content */}
              <AdSenseAd
                adSlot="2631348401"
                accountTier={accountTier}
                className="mt-4"
              />
            </>
          )}
        </section>

        <RecipeMetaSidebar
          prepTime={recipe.prepTime}
          cookTime={recipe.cookTime}
          servings={recipe.servings}
          ingredients={ingredientsList}
        />
      </div>

      <RecipeImageEditDialog
        open={imageEditDialogOpen}
        onOpenChange={setImageEditDialogOpen}
        currentImageUrl={recipe?.imageUrl ?? null}
        title={recipe?.title ?? ""}
        imageHint={recipe?.imageHint}
        imageFocalPointX={recipe?.imageFocalPointX}
        imageFocalPointY={recipe?.imageFocalPointY}
        imageZoom={recipe?.imageZoom}
        onImageSelected={handleImageSelected}
        onRemoveImage={toggleRemoveImage}
        onSave={handleImageSave}
        onCancel={handleImageEditCancel}
        onCropSave={async (focalPointX, focalPointY, zoom) => {
          if (!recipe || !user) return;
          try {
            await updateRecipe(user.uid, recipe.id, {
              imageFocalPointX: focalPointX,
              imageFocalPointY: focalPointY,
              imageZoom: zoom,
            }, recipeBox);
            toast({
              title: "Image position updated",
              description: "Your crop has been saved.",
            });
          } catch (err) {
            console.error("Failed to save crop", err);
            toast({
              variant: "destructive",
              title: "Could not save position",
              description: "Please try again.",
            });
          }
        }}
        imageFile={imageFile}
        imagePreviewUrl={imagePreviewUrl}
        removeImage={removeImage}
        isSubmitting={recipeForm.formState.isSubmitting}
      />
    </div>
  );
}

